import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import MessageInput from '../MessageInput/MessageInput.js';
import ReactionButtons from './ReactionButtons.js';
import ChatBubble from './ChatBubble.js';
import axiosInstance from '../../api/axiosConfig.js';
import ReportTable from './ReportTable.js';
import '../../styles/css/Chat.css';

const LoadingDots = () => {
  return <span className="dot">...</span>;
};

// Stable UUID for grouping a conversation's reactions; falls back through
// getRandomValues then Math.random if crypto.randomUUID is unavailable
// (non-secure context / old browser), never a constant or a throw.
const genSessionId = () => {
  const c = window.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const nibble = () => (c?.getRandomValues
    ? c.getRandomValues(new Uint8Array(1))[0]
    : Math.floor(Math.random() * 256)) % 16;
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = nibble();
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
};

// A cancellation error shaped like axios's CanceledError so the send handler's
// existing `code === 'ERR_CANCELED'` checks treat a stop during token fetch the
// same as any other stop.
const makeCanceled = () => {
  const err = new Error('canceled');
  err.code = 'ERR_CANCELED';
  return err;
};

const makeTokenError = (message = '') => {
  const err = new Error(message);
  err.code = 'ERR_TOKEN';
  return err;
};

// Resolve the per-request access token, but reject immediately (as a stop) if
// the abort signal fires first. Lets Stop break out even while a slow getToken()
// is pending, and makes a token rejection that lands after a stop a no-op.
const acquireToken = (agentConfig, signal) => {
  // Bail before invoking getToken() if already aborted, so no orphan token
  // promise is left without a rejection handler.
  if (signal?.aborted) return Promise.reject(makeCanceled());
  const source = agentConfig.getToken
    ? Promise.resolve().then(() => agentConfig.getToken())
      .then((result) => {
        // Keep legacy string callbacks valid.
        const token = typeof result === 'string'
          ? result
          : (result?.success === true ? result.access_token : null);
        if (typeof token === 'string' && token.trim()) return token;
        throw makeTokenError(
          typeof result?.error_text === 'string' ? result.error_text.trim() : ''
        );
      }, () => {
        // Callback exceptions may contain credentials; show a generic auth notice.
        throw makeTokenError();
      })
    : Promise.resolve(agentConfig.access_token || null);
  if (!signal) return source;
  return new Promise((resolve, reject) => {
    const onAbort = () => reject(makeCanceled());
    signal.addEventListener('abort', onAbort, { once: true });
    source.then(resolve, reject).finally(() => {
      signal.removeEventListener('abort', onAbort);
    });
  });
};

const Chat = ({ userMessage, setUserMessage, userLanguage, host}) => {
  const [messages, setMessages] = useState([]);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [messageReactions, setMessageReactions] = useState({}); // Store reactions by message index
  const [tokenErrorMessage, setTokenErrorMessage] = useState('');
  const [isSending, setIsSending] = useState(false); // Drives the send button's disabled state
  const sendingRef = useRef(false); // Synchronous in-flight latch guarding concurrent sends
  const abortRef = useRef(null); // AbortController for the in-flight agent request; abort() stops the turn
  // Per-conversation id grouping reactions; regenerated on reset. Ref-only — it
  // is read by async handlers to detect a reset, never rendered, so no state.
  const sessionIdRef = useRef(null);
  if (sessionIdRef.current === null) sessionIdRef.current = genSessionId();
  const [modelOptions, setModelOptions] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');

  useEffect(() => {
    let cancelled = false;
    axiosInstance.get('/api/models')
      .then((res) => {
        if (cancelled) return;
        const opts = res.data?.options || [];
        setModelOptions(opts);
        const def = res.data?.default;
        if (def && opts.some((o) => o.id === def)) {
          setSelectedModel(def);
        } else if (opts.length > 0) {
          setSelectedModel(opts[0].id);
        }
      })
      .catch((err) => {
        console.error('Failed to load /api/models', err);
      });
    return () => { cancelled = true; };
  }, []);
  
  // Use userLanguage from profile (ru, ua, en) - no language selection in UI
  // Map 'ua' to 'uk' for compatibility with translations (ua from profile → uk in translations)
  const language = userLanguage === 'ua' ? 'uk' : (userLanguage || 'en');

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Show welcome message on component mount using language from profile
  useEffect(() => {
    scrollToBottom();
      const greetings = {
        ru: `### Привет! 👋 Я помогу с отчетами.

Вот что я умею:
- Показать продажи за период в штуках, ящиках, кг или деньгах дистрибьютора
- Детализировать до продуктов, брендов, дистрибьюторов, торговых точек, агентов
- Сортировать данные и строить топ-N
- Сравнивать периоды и считать прирост
- Строить динамику по месяцам или годам

Например:
- «Покажи продажи в деньгах за текущий месяц»
- «Покажи топ-10 продуктов за текущий год в килограммах»
- «Покажи продажи в штуках в динамике по месяцам за текущий год»
`,
        uk: `### Привіт! 👋 Я допоможу з звітами.
        
Ось що я вмію:
- Показати продажі за період в одиницях, коробках, кг або грошах дистриб'ютора
- Деталізувати до продуктів, брендів, дистриб'юторів, торгових точок, агентів
- Сортувати дані та будувати топ-N
- Порівнювати періоди та рахувати приріст
- Будувати динаміку по місяцях або роках

Наприклад:
- «Покажи продажі в грошах за поточний місяць»
- «Покажи топ-10 продуктів за поточний рік у кілограмах»
- «Покажи продажі в одиницях в динаміці по місяцях за поточний рік»`,
        en: `### Hi! 👋 I can help with reports.
        
Here's what I can do:
- Show sales for a period in units, boxes, kg, or distributor's currency
- Break down by products, brands, distributors, retail outlets, agents
- Sort data and build top-N
- Compare periods and calculate growth
- Build dynamics by months or years

For example:

- "Show sales in money for the current month"
- "Show top-10 products for the current year in kilograms"
- "Show sales in units in dynamics by months for the current year"`
      };

    const welcomeMessage = {
      sender: 'server',
      message: greetings[language] || greetings['en'], 
    };

    setMessages([welcomeMessage]);
    // A language switch resets the conversation: wipe history + reaction
    // highlights and start a fresh reaction session. Update the ref
    // synchronously so an in-flight send/reaction resolving after this point
    // sees the new generation and drops its now-stale result.
    setConversationHistory([]);
    setMessageReactions({});
    setTokenErrorMessage('');
    sessionIdRef.current = genSessionId();
    // Abort any in-flight request so a language switch doesn't leave a zombie
    // run waiting; its result would be dropped by the generation gate anyway.
    if (abortRef.current) abortRef.current.abort();
    // Release the send latch so the fresh conversation is usable immediately.
    // A now-abandoned in-flight request won't stomp a new send: its finally is
    // generation-gated on the session captured when it started.
    sendingRef.current = false;
    setIsSending(false);
  }, [language]);

  const handleSendMessage = async () => {
    if (sendingRef.current || !userMessage.trim()) return;
    const sendSession = sessionIdRef.current; // conversation generation at send time

    const connectionErrorMessages = {
      ru: 'Произошла ошибка соединения с сервером. Повторите попытку позже.',
      uk: "Сталася помилка з'єднання з сервером. Повторіть спробу пізніше.",
      en: 'A connection error occurred with the server. Please try again later.',
    };

    const tokenErrorMessages = {
      ru: 'Не удалось получить токен доступа. Повторите попытку или войдите в систему заново.',
      uk: 'Не вдалося отримати токен доступу. Спробуйте ще раз або увійдіть у систему повторно.',
      en: 'Unable to get an access token. Please try again or sign in again.',
    };

    const stoppedMessages = {
      ru: 'Генерация ответа остановлена.',
      uk: 'Генерацію відповіді зупинено.',
      en: 'Response generation stopped.',
    };

    // Latch synchronously (ref) so a same-tick double-fire can't slip through;
    // isSending drives the button's disabled UI.
    sendingRef.current = true;
    setIsSending(true);
    try {
      const newMessage = { sender: 'user', message: userMessage };
      setMessages((prevMessages) => [...prevMessages, newMessage]);

      const waitingMessage = { sender: 'server', message: <LoadingDots /> };
      setMessages((prevMessages) => [...prevMessages, waitingMessage]);

      // Fresh AbortController per send, created BEFORE the first await so the
      // stop button (already shown via isSending) can cancel even while the
      // token is being fetched, and so a stale send resuming after a reset
      // can't stomp a newer send's controller.
      const controller = new AbortController();
      abortRef.current = controller;

      // Per-user spot2d JWT injected by the host page. Prefer getToken() so the
      // host can hand back a freshly refreshed token mid-conversation. Raced
      // against the abort signal so Stop breaks out even if getToken() is slow;
      // a refresh failure shows an auth notice without starting an API request.
      const agentConfig = window.__AI_AGENT_CONFIG__ || {};
      const accessToken = await acquireToken(agentConfig, controller.signal);

      // A reset during token acquisition abandons this send and its UI updates.
      if (sessionIdRef.current !== sendSession) return;
      if (controller.signal.aborted) throw makeCanceled();
      setTokenErrorMessage('');
      // Keep the question until auth succeeds, without clearing a newer draft.
      setUserMessage((current) => current === userMessage ? '' : current);

      const requestData = {
        user_message: userMessage,
        // Forwarded as Bearer on the backend's MCP call (multitenant).
        access_token: accessToken,
        conversation_history: conversationHistory,
        // New parameters: host and user_language from user profile
        host: host || null,
        user_language: userLanguage || null,
        model: selectedModel || null,
        // Groups this turn's LangSmith run into a thread; same id /api/reaction
        // uses, so a rating and its run point at the same conversation.
        session_id: sendSession,
      };

      const sendRequest = () => axiosInstance.post('/api/query', requestData, { signal: controller.signal });

      let response;
      try {
        response = await sendRequest();
      } catch (firstError) {
        // A user-initiated stop (abort) is never retried.
        if (firstError.code === 'ERR_CANCELED') throw firstError;
        // Retry only a genuine connection failure (a blip while reaching the server).
        const isConnectionError = !firstError.response
          && firstError.code !== 'ECONNABORTED'
          && firstError.code !== 'ETIMEDOUT';
        if (!isConnectionError) throw firstError;
        await new Promise((resolve) => setTimeout(resolve, 1000));

        // A conversation reset during the wait abandons this send; skip the
        // retry (a full agent run) since its result would be dropped anyway.
        if (sessionIdRef.current !== sendSession) return;
        response = await sendRequest();
      }

      // Debug: Log the response data
      console.log('Response data:', {
        message: response.data.message,
        processed_report: response.data.processed_report ? 'Has processed report data' : 'No processed report data',
        processed_length: response.data.processed_report ? response.data.processed_report.split('\n').length : 0
      });

      // If the conversation was reset (e.g. language switch) while this request
      // was in flight, drop the now-stale result instead of writing it back.
      if (sessionIdRef.current !== sendSession) return;

      const reportData = response.data.processed_report;

      // Append this turn to history as standard user/assistant messages,
      // trimming and skipping blanks to match the backend.
      const historyForSend = [...conversationHistory];
      if (userMessage.trim()) {
        historyForSend.push({ role: 'user', content: userMessage.trim() });
      }
      const reply = (response.data.message || '').trim();
      if (reply) {
        // The id is what the next turn chains onto
        const reportId = response.data.report_id || null;
        historyForSend.push({
          role: 'assistant',
          content: reply,
          report_generated: !!(reportId || response.data.processed_report),
          report_id: reportId,
        });
      }
      setConversationHistory(historyForSend);
      
      const serverMessage = {
        sender: 'server',
        message: response.data.message,
        report: reportData,
        isAnswer: Boolean(response.data.message), // only real, non-empty answers are rateable
      };
      setMessages((prevMessages) => {
        const newMessages = [...prevMessages];
        newMessages[newMessages.length - 1] = serverMessage;
        return newMessages;
      });
    } catch (error) {
      // Conversation reset mid-flight — don't overwrite the fresh chat with a
      // stale error/stopped bubble.
      if (sessionIdRef.current !== sendSession) return;
      // A user-initiated stop aborts the request; show a neutral notice, not an error.
      const stopped = error.code === 'ERR_CANCELED';
      const tokenError = error.code === 'ERR_TOKEN';
      if (tokenError) {
        setTokenErrorMessage(error.message || tokenErrorMessages[language] || tokenErrorMessages['en']);
        // Remove this unsent turn's question and loading bubble; the draft stays.
        setMessages((prevMessages) => prevMessages.slice(0, -2));
        return;
      }
      if (!stopped) {
        console.error('Error sending message:', error);
        console.error('Error sending request:', conversationHistory);
      }
      const noticeMessage = {
        sender: 'server',
        message: stopped
          ? (stoppedMessages[language] || stoppedMessages['en'])
          : (connectionErrorMessages[language] || connectionErrorMessages['en']),
      };
      // Replace the loading message with the notice
      setMessages((prevMessages) => {
        if (prevMessages.length === 0) return [noticeMessage];
        const newMessages = [...prevMessages];
        newMessages[newMessages.length - 1] = noticeMessage;
        return newMessages;
      });
    } finally {
      // Only release the latch if we still own it — a mid-flight conversation
      // reset already released it and may have started a new send whose latch
      // we must not stomp.
      if (sessionIdRef.current === sendSession) {
        sendingRef.current = false;
        setIsSending(false);
        abortRef.current = null;
      }
    }
  };

  // Stop the in-flight turn. Aborting rejects the axios promise, whose catch
  // clears the loading bubble and whose finally releases the send latch.
  const handleStopMessage = () => {
    if (abortRef.current) abortRef.current.abort();
  };

  const handleReaction = async (reaction, messageIndex) => {
    const reactSession = sessionIdRef.current; // conversation generation at click time
    try {
      const botMsg = messages[messageIndex];
      // The prompt that produced this answer: nearest preceding user message.
      let userText = null;
      for (let i = messageIndex - 1; i >= 0; i--) {
        if (messages[i].sender === 'user') {
          userText = messages[i].message;
          break;
        }
      }

      await axiosInstance.post('/api/reaction', {
        reaction,
        session_id: reactSession,
        message_index: messageIndex,
        user_message: userText,
        bot_message: typeof botMsg?.message === 'string' ? botMsg.message : null,
        bot_csv: botMsg?.report || null,
      });

      // Skip the local UI update if the conversation was reset mid-request.
      if (sessionIdRef.current !== reactSession) return;
      // Update reaction state for this message
      setMessageReactions(prev => ({
        ...prev,
        [messageIndex]: reaction
      }));
    } catch (error) {
      console.error('Error sending reaction:', error);
      throw error; // Re-throw to let ReactionButtons handle it
    }
  };

  const modelSelectLabel = {
    ru: 'Модель',
    uk: 'Модель',
    en: 'Model',
  }[language] || 'Model';

  return (
    <div className="chat-container">
      {modelOptions.length > 0 && (
        <div className="chat-model-selector">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="model-select-label">{modelSelectLabel}</InputLabel>
            <Select
              labelId="model-select-label"
              value={selectedModel}
              label={modelSelectLabel}
              onChange={(e) => setSelectedModel(e.target.value)}
              // Stable hook for host-page CSS on the menu root (the id="menu-" element)
              MenuProps={{ className: 'models-dropdown-menu-box' }}
            >
              {modelOptions.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      )}
      <div className="chat-box">
        {messages.map((msg, index) => (
          <div key={index}>
            {/* Применяем анимацию к сообщению от бота */}
            <ChatBubble
              message={
                typeof msg.message === 'string' ? (
                  <ReactMarkdown>{msg.message}</ReactMarkdown>
                ) : (
                  msg.message
                )
              }
              sender={msg.sender}
              animationClass={msg.sender === 'server' ? 'message-animation' : ''}
            />
            {msg.report && <ReportTable data={msg.report} language={language}/>}
            {msg.sender === 'server' && msg.isAnswer && (
              <div className="chat-reaction-container">
                <ReactionButtons
                  onReaction={(reaction) => handleReaction(reaction, index)}
                  initialReaction={messageReactions[index]}
                />
              </div>
            )}
          </div>
        ))}
        {tokenErrorMessage && (
          <Alert severity="error" className="token-message-error" role="alert">
            {tokenErrorMessage}
          </Alert>
        )}
        <div ref={messagesEndRef} />
      </div>
      <MessageInput value={userMessage} onChange={(e) => setUserMessage(e.target.value)} onSend={handleSendMessage} onStop={handleStopMessage} language={language} sending={isSending}/>
    </div>
  );
};

export default Chat;
